// Operating cadence endpoints (MEETINGS; 06 §Cadence screens "Issues", "Meeting", "Vision"; 02
// §Operating cadence; 05 `issue`, `meeting / meeting_series`, `decision`). Issues are listed per
// team (or the company), moved identify → discuss → solve, escalated team → company and resolved
// into tasks (7-day default due) or a decision. A weekly team meeting is a series with meetings:
// the agenda is built from the live data (scorecard misses → off-track priorities → top issues →
// due to-dos → custom), the meeting runs with section timers, a side panel shows each item's
// source as a live card, outcomes are captured inline, and its end posts a summary to the team
// channel's topic "Meeting {date}". The vision page is a `page` of kind `vision` with the five
// sections of 06 (core values, focus, long-term target, 3-year picture, 1-year plan).
//
// Every route is `core`: the cadence screens open beside the chart (priorities are core too).
import { z } from "zod";
import {
  AgendaConfig,
  Issue,
  Meeting,
  MeetingAgendaItem,
  MeetingOutcome,
  MeetingSeries,
} from "../cadence-rows";
import {
  type AgendaSection,
  GoalTrack,
  IssueSourceKind,
  type IssueStatus,
  MeetingPrereadState,
  MeetingSeriesKind,
  MeetingState,
  MetricUnit,
} from "../enums";
import { Decision } from "../memory-rows";
import { LIMITS, PeriodKey, Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { GoalDetail } from "./org";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

const Title = z.string().trim().min(1).max(500);
const LongText = z.string().max(LIMITS.longText);
const Notes = z.string().max(20_000);

// Issues ----------------------------------------------------------------------------------------

/**
 * An issue as the board shows it: the row, its age in days (since raised; resolved ones stop at
 * their resolution), the names it shows, and what the caller may do with it (05 `issue`: editor
 * = owner#holder | team#lead#holder | raiser, plus managers; escalate = team#lead#holder |
 * owner#holder, plus managers).
 */
export const IssueView = Issue.extend({
  age_days: z.int().min(0),
  team_name: z.string().nullable(),
  owner_title: z.string().nullable(),
  owner_name: z.string().nullable(),
  raised_by_name: z.string().nullable(),
  can_edit: z.boolean(),
  can_escalate: z.boolean(),
}).meta({ id: "IssueView" });
export type IssueView = z.infer<typeof IssueView>;

export const IssueListQuery = z
  .object({
    /** A team's list; `company` for the company-level list (escalated or raised there). */
    team_id: z.union([Uuid, z.literal("company")]).optional(),
    /** Resolved and dropped issues of the last 30 days as well (default false). */
    include_closed: z.enum(["true", "false"]).optional(),
    /** Archived issues instead of the live ones (default false). */
    archived: z.enum(["true", "false"]).optional(),
  })
  .meta({ id: "IssueListQuery" });

/**
 * The issues the caller may see (05 `issue` viewer = team#viewer: a team's issues reach the
 * holders of its seats; company-level ones every member; managers all), the teams it may raise
 * on, and whether it may raise company-level issues.
 */
export const IssueBoard = z
  .object({
    data: z.array(IssueView),
    teams: z.array(z.object({ id: Uuid, name: z.string(), can_raise: z.boolean() })),
    can_raise_company: z.boolean(),
  })
  .meta({ id: "IssueBoard" });

export const IssueCreate = z
  .strictObject({
    /** The team whose list it joins; null or absent: the company's list. */
    team_id: Uuid.nullable().optional(),
    title: Title,
    body: LongText.nullable().optional(),
    owner_seat_id: Uuid.nullable().optional(),
    priority: z.int().min(1).max(3).optional(),
    /** What it was raised from (a chat message, a task, a missed metric, an off-track priority). */
    source_kind: IssueSourceKind.optional(),
    source_id: Uuid.nullable().optional(),
    /** The line the card shows for its source ("Placements · W42: 3 of 5"). */
    source_label: z.string().max(LIMITS.name).nullable().optional(),
  })
  .meta({ id: "IssueCreate" });

export const IssuePatch = z
  .strictObject({
    title: Title.optional(),
    body: LongText.nullable().optional(),
    owner_seat_id: Uuid.nullable().optional(),
    priority: z.int().min(1).max(3).optional(),
    team_id: Uuid.nullable().optional(),
    /** identify, discuss, solve; `dropped` drops it; a resolved issue moved back reopens it. */
    status: z.enum(["identify", "discuss", "solve", "dropped"]).optional(),
    /** The card's place in its status column (0 = top); the others shift. */
    position: z.int().min(0).max(10_000).optional(),
    archived: z.boolean().optional(),
  })
  .meta({ id: "IssuePatch" });

/** Escalate up the chart (team → company), or hand back to the team. */
export const IssueEscalate = z
  .strictObject({
    /** FOUNDER-OS: `portfolio` lifts it to the account's Founder OS (head seat or a manager). */
    to: z.enum(["company", "team", "portfolio"]),
  })
  .meta({ id: "IssueEscalate" });

/** A to-do a resolution or a meeting creates: due in 7 days unless given. */
export const TodoInput = z
  .strictObject({
    title: Title,
    assignee_seat_id: Uuid.nullable().optional(),
    due_at: Timestamp.nullable().optional(),
  })
  .meta({ id: "TodoInput" });

export const DecisionInput = z
  .strictObject({
    /** The log's title (default: the issue's or the agenda item's). */
    title: Title.optional(),
    decision: z.string().trim().min(1).max(LIMITS.text),
    why: z.string().max(LIMITS.text).nullable().optional(),
    /** RECALL-INBOX-LOOP: what is expected to happen; the check card asks whether it did. */
    expected_outcome: z.string().trim().min(1).max(500).nullable().optional(),
    /** Days until that check (default 14). */
    check_in_days: z.int().min(1).max(365).optional(),
  })
  .meta({ id: "DecisionInput" });

/**
 * Solve an issue: `task` creates the to-dos (7-day default due, origin `issue`), `decision`
 * writes the decision log (source `issue`), `none` just closes it.
 */
export const IssueResolve = z
  .strictObject({
    kind: z.enum(["task", "decision", "none"]),
    tasks: z.array(TodoInput).min(1).max(10).optional(),
    decision: DecisionInput.optional(),
    note: z.string().max(LIMITS.text).nullable().optional(),
  })
  .meta({ id: "IssueResolve" });

// Meeting series and meetings -------------------------------------------------------------------

/** A meeting in a list: when, its state, and how much it holds. */
export const MeetingSummary = z
  .object({
    id: Uuid,
    series_id: Uuid,
    scheduled_at: Timestamp,
    started_at: Timestamp.nullable(),
    ended_at: Timestamp.nullable(),
    state: MeetingState,
    rating: z.int().nullable(),
    items: z.int().min(0),
    outcomes: z.int().min(0),
    preread_state: MeetingPrereadState,
  })
  .meta({ id: "MeetingSummary" });
export type MeetingSummary = z.infer<typeof MeetingSummary>;

export const MeetingSeriesView = MeetingSeries.extend({
  team_name: z.string().nullable(),
  can_edit: z.boolean(),
  next_meeting: MeetingSummary.nullable(),
  last_meeting: MeetingSummary.nullable(),
}).meta({ id: "MeetingSeriesView" });
export type MeetingSeriesView = z.infer<typeof MeetingSeriesView>;

export const MeetingSeriesList = z
  .object({
    data: z.array(MeetingSeriesView),
    /** The teams the caller may add a series for (05: the team lead's holder, managers). */
    teams: z.array(z.object({ id: Uuid, name: z.string(), can_create: z.boolean() })),
  })
  .meta({ id: "MeetingSeriesList" });

const SeriesFields = {
  name: z.string().trim().min(1).max(LIMITS.name),
  kind: MeetingSeriesKind.optional(),
  /** RFC 5545 RRULE in the company's time zone (`FREQ=WEEKLY;BYDAY=TU;BYHOUR=10;BYMINUTE=0`). */
  schedule: z.string().min(1).max(LIMITS.name),
  duration_minutes: z.int().min(5).max(600).optional(),
  attendees: z.array(Uuid).max(LIMITS.list).optional(),
  facilitator_seat_id: Uuid.nullable().optional(),
  agenda_config: AgendaConfig.optional(),
  channel_id: Uuid.nullable().optional(),
  board_id: Uuid.nullable().optional(),
  preread_by_membership_id: Uuid.nullable().optional(),
};

export const MeetingSeriesCreate = z
  .strictObject({
    team_id: Uuid.nullable().optional(),
    ...SeriesFields,
    /** The first meeting (default: the schedule's next occurrence). */
    first_meeting_at: Timestamp.optional(),
  })
  .meta({ id: "MeetingSeriesCreate" });

export const MeetingSeriesPatch = z
  .strictObject({
    name: SeriesFields.name.optional(),
    schedule: SeriesFields.schedule.optional(),
    duration_minutes: SeriesFields.duration_minutes,
    attendees: SeriesFields.attendees,
    facilitator_seat_id: SeriesFields.facilitator_seat_id,
    agenda_config: SeriesFields.agenda_config,
    channel_id: SeriesFields.channel_id,
    board_id: SeriesFields.board_id,
    preread_by_membership_id: SeriesFields.preread_by_membership_id,
    archived: z.boolean().optional(),
  })
  .meta({ id: "MeetingSeriesPatch" });

export const MeetingList = z.object({ data: z.array(MeetingSummary) }).meta({ id: "MeetingList" });

export const MeetingCreate = z
  .strictObject({ scheduled_at: Timestamp.optional() })
  .meta({ id: "MeetingCreate" });

/** A task as an agenda card shows it. */
export const AgendaTaskCard = z
  .object({
    id: Uuid,
    number: z.int(),
    title: z.string(),
    status: z.string(),
    done: z.boolean(),
    due_at: Timestamp.nullable(),
    board_id: Uuid,
    assignee_seat_ids: z.array(Uuid),
  })
  .meta({ id: "AgendaTaskCard" });

/**
 * The live card of an agenda item's source, read when the meeting is read (never a snapshot):
 * the metric's week on its seat with the trend, the priority with its milestones, the issue, the
 * to-do. `null` for a custom line, or a source the caller may not read or that is gone.
 */
export const AgendaSourceCard = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("metric"),
      metric_def_id: Uuid,
      name: z.string(),
      unit: MetricUnit,
      seat_id: Uuid,
      seat_title: z.string(),
      holder_name: z.string().nullable(),
      period_key: PeriodKey,
      actual: z.number().nullable(),
      target: z.number().nullable(),
      attainment: z.number().nullable(),
      trend: z
        .array(
          z.object({
            period_key: PeriodKey,
            actual: z.number().nullable(),
            target: z.number().nullable(),
          }),
        )
        .max(8),
    }),
    z.object({
      kind: z.literal("goal"),
      goal: GoalDetail,
      seat_title: z.string(),
      holder_name: z.string().nullable(),
    }),
    z.object({ kind: z.literal("issue"), issue: IssueView }),
    z.object({ kind: z.literal("task"), task: AgendaTaskCard }),
  ])
  .meta({ id: "AgendaSourceCard" });
export type AgendaSourceCard = z.infer<typeof AgendaSourceCard>;

export const AgendaItemView = MeetingAgendaItem.extend({
  source: AgendaSourceCard.nullable(),
}).meta({ id: "AgendaItemView" });
export type AgendaItemView = z.infer<typeof AgendaItemView>;

/** RECALL-KPIS-MIRROR: a meeting's memory input (`meeting.preread_memory`). */
export const MeetingPrereadMemory = z
  .object({
    state: z.enum(["read", "unavailable"]),
    read_at: Timestamp,
    digest: z.string().nullable(),
    agenda: z.string().nullable(),
    detail: z.string().nullable(),
  })
  .meta({ id: "MeetingPrereadMemory" });
export type MeetingPrereadMemory = z.infer<typeof MeetingPrereadMemory>;

/** RECALL-KPIS-MIRROR: one person's stance on a logged decision, with their name. */
export const DecisionStanceItem = z
  .object({
    id: Uuid,
    decision_id: Uuid,
    membership_id: Uuid,
    name: z.string(),
    stance: z.enum(["ratify", "dissent"]),
    note: z.string().nullable(),
    /** What memory did with it: `sent`, `waiting`, `refused`, or `local` (no memory record). */
    recall_state: z.enum(["sent", "waiting", "refused", "local"]),
    recall_detail: z.string().nullable(),
    updated_at: Timestamp,
  })
  .meta({ id: "DecisionStanceItem" });
export type DecisionStanceItem = z.infer<typeof DecisionStanceItem>;

export const DecisionStances = z
  .object({
    decision: Decision,
    stances: z.array(DecisionStanceItem),
    /** The caller's own stance, null when none. */
    mine: DecisionStanceItem.nullable(),
    /** Whether the caller may take a stance (a person who can see the decision). */
    can_stance: z.boolean(),
  })
  .meta({ id: "DecisionStances" });
export type DecisionStances = z.infer<typeof DecisionStances>;

export const DecisionStanceBody = z
  .strictObject({
    stance: z.enum(["ratify", "dissent"]),
    /** Why, for a dissent (1 to 300 characters); never sent with a ratify. */
    note: z.string().trim().min(1).max(300).optional(),
  })
  .refine((b) => (b.stance === "dissent") === (b.note !== undefined), {
    message: "a dissent says why (note); a ratify has no note",
  })
  .meta({ id: "DecisionStanceBody" });
export type DecisionStanceBody = z.infer<typeof DecisionStanceBody>;

/**
 * A meeting with everything the meeting screen shows: the series, the agenda with live source
 * cards (built from the live data; routine task changes never add lines), the outcomes captured,
 * the decisions they logged, the pre-read (the agent asked, whether it is live), where the
 * summary posts, and whether the caller may run it (05: facilitator#holder | team#lead#holder,
 * plus managers).
 */
export const MeetingDetail = z
  .object({
    meeting: Meeting,
    series: MeetingSeries.extend({ team_name: z.string().nullable() }),
    agenda: z.array(AgendaItemView),
    outcomes: z.array(MeetingOutcome),
    decisions: z.array(Decision),
    can_edit: z.boolean(),
    preread: z.object({
      state: MeetingPrereadState,
      agent: z.object({ membership_id: Uuid, name: z.string(), live: z.boolean() }).nullable(),
      requested_at: Timestamp.nullable(),
      note: z.string().nullable(),
      /**
       * RECALL-KPIS-MIRROR: what memory said before the meeting (Recall's team digest and agenda,
       * read by the pre-read job), input only; the agenda above is CompanyOS's. Null: not read.
       */
      memory: MeetingPrereadMemory.nullable(),
    }),
    /** The channel the summary posts to (the team's, or the series'), and its topic name. */
    channel: z.object({ id: Uuid, name: z.string(), topic: z.string() }).nullable(),
    previous_meeting_id: Uuid.nullable(),
    next_meeting_id: Uuid.nullable(),
  })
  .meta({ id: "MeetingDetail" });
export type MeetingDetail = z.infer<typeof MeetingDetail>;

export const MeetingPatch = z
  .strictObject({
    notes: Notes.optional(),
    preread_note: Notes.nullable().optional(),
    /** The live item: its timer starts now (null stops the timers). */
    current_item_id: Uuid.nullable().optional(),
    rating: z.int().min(1).max(10).optional(),
  })
  .meta({ id: "MeetingPatch" });

export const MeetingItemParams = z
  .object({ co: Uuid, id: Uuid, item_id: Uuid })
  .meta({ id: "MeetingItemParams" });

export const AgendaItemCreate = z
  .strictObject({
    title: Title,
    section: z.enum(["custom", "headlines"]).optional(),
    time_box_s: z.int().min(0).max(36_000).optional(),
  })
  .meta({ id: "AgendaItemCreate" });

export const AgendaItemPatch = z
  .strictObject({
    title: Title.optional(),
    time_box_s: z.int().min(0).max(36_000).optional(),
    position: z.int().min(0).max(1000).optional(),
    done: z.boolean().optional(),
    elapsed_s: z.int().min(0).max(360_000).optional(),
  })
  .meta({ id: "AgendaItemPatch" });

export const MeetingEnd = z
  .strictObject({
    notes: Notes.optional(),
    rating: z.int().min(1).max(10).optional(),
  })
  .meta({ id: "MeetingEnd" });

/** An outcome captured inline: a to-do, a decision, an issue resolved, an issue raised. */
export const MeetingOutcomeCreate = z
  .discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("task"),
      item_id: Uuid.nullable().optional(),
      title: Title,
      assignee_seat_id: Uuid.nullable().optional(),
      due_at: Timestamp.nullable().optional(),
    }),
    z.strictObject({
      kind: z.literal("decision"),
      item_id: Uuid.nullable().optional(),
      title: Title.optional(),
      decision: z.string().trim().min(1).max(LIMITS.text),
      why: z.string().max(LIMITS.text).nullable().optional(),
      expected_outcome: z.string().trim().min(1).max(500).nullable().optional(),
      check_in_days: z.int().min(1).max(365).optional(),
    }),
    z.strictObject({
      kind: z.literal("issue_resolved"),
      item_id: Uuid.nullable().optional(),
      issue_id: Uuid,
      note: z.string().max(LIMITS.text).nullable().optional(),
    }),
    z.strictObject({
      kind: z.literal("issue_raised"),
      item_id: Uuid.nullable().optional(),
      title: Title,
      owner_seat_id: Uuid.nullable().optional(),
      priority: z.int().min(1).max(3).optional(),
    }),
  ])
  .meta({ id: "MeetingOutcomeCreate" });

// Vision ----------------------------------------------------------------------------------------

/** 06 §Cadence "Vision page": the template's sections. */
export const VisionSectionKey = z
  .enum(["core_values", "focus", "long_term_target", "three_year_picture", "one_year_plan"])
  .meta({ id: "VisionSectionKey" });
export type VisionSectionKey = z.infer<typeof VisionSectionKey>;

export const VisionView = z
  .object({
    /** The vision page (`page.kind = vision`); null until first saved. */
    page_id: Uuid.nullable(),
    version: z.int().min(0),
    updated_at: Timestamp.nullable(),
    sections: z.array(z.object({ key: VisionSectionKey, label: z.string(), text: z.string() })),
    can_edit: z.boolean(),
    /** The current quarter's priorities the 1-year plan links to. */
    priorities: z.array(
      z.object({ id: Uuid, title: z.string(), track: GoalTrack, seat_id: Uuid, state: z.string() }),
    ),
    quarter: PeriodKey,
  })
  .meta({ id: "VisionView" });
export type VisionView = z.infer<typeof VisionView>;

const VisionText = z.string().max(LIMITS.longText).optional();

export const VisionUpdate = z
  .strictObject({
    sections: z.strictObject({
      core_values: VisionText,
      focus: VisionText,
      long_term_target: VisionText,
      three_year_picture: VisionText,
      one_year_plan: VisionText,
    }),
    /** The version read (409 when someone saved since). */
    version: z.int().min(0).optional(),
  })
  .meta({ id: "VisionUpdate" });

// Routes ----------------------------------------------------------------------------------------

const issueEmits = ["issue.updated", "issue.status_changed", "issue.dropped"] as const;

export const cadenceRoutes = {
  "issues.list": defineRoute({
    ...core,
    id: "issues.list",
    method: "get",
    path: `${base}/issues`,
    tag: "cadence",
    summary: "The issues lists the caller may see, by team (and the company's)",
    access: "viewer",
    params: CompanyParams,
    query: IssueListQuery,
    response: { status: 200, schema: IssueBoard },
    errors: [],
    emits: [],
  }),
  "issues.create": defineRoute({
    ...core,
    id: "issues.create",
    method: "post",
    path: `${base}/issues`,
    tag: "cadence",
    summary: "Raise an issue on a team's list (or the company's)",
    access: "viewer",
    params: CompanyParams,
    body: IssueCreate,
    idempotency: "optional",
    response: { status: 201, schema: IssueView },
    errors: ["validation_failed"],
    emits: ["issue.raised"],
  }),
  "issues.update": defineRoute({
    ...core,
    id: "issues.update",
    method: "patch",
    path: `${base}/issues/{id}`,
    tag: "cadence",
    summary: "Edit an issue, move it between statuses, reorder, drop or archive it",
    access: "viewer",
    params: CompanyItemParams,
    body: IssuePatch,
    response: { status: 200, schema: IssueView },
    errors: ["validation_failed"],
    emits: [...issueEmits],
  }),
  "issues.delete": defineRoute({
    ...core,
    id: "issues.delete",
    method: "delete",
    path: `${base}/issues/{id}`,
    tag: "cadence",
    summary: "Delete an issue (archiving is the default)",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 204 },
    errors: [],
    emits: ["issue.deleted"],
  }),
  "issues.escalate": defineRoute({
    ...core,
    id: "issues.escalate",
    method: "post",
    path: `${base}/issues/{id}/escalate`,
    tag: "cadence",
    summary: "Escalate an issue to the company, or hand it back to its team",
    access: "viewer",
    params: CompanyItemParams,
    body: IssueEscalate,
    response: { status: 200, schema: IssueView },
    errors: ["validation_failed"],
    emits: ["issue.escalated", "issue.deescalated"],
  }),
  "issues.resolve": defineRoute({
    ...core,
    id: "issues.resolve",
    method: "post",
    path: `${base}/issues/{id}/resolve`,
    tag: "cadence",
    summary: "Solve an issue: to-dos (7-day default due), a decision, or nothing",
    access: "viewer",
    params: CompanyItemParams,
    body: IssueResolve,
    idempotency: "optional",
    response: { status: 200, schema: IssueView },
    errors: ["validation_failed", "conflict"],
    emits: ["issue.resolved", "task.created", "decision.recorded"],
  }),
  "meetingSeries.list": defineRoute({
    ...core,
    id: "meetingSeries.list",
    method: "get",
    path: `${base}/meeting-series`,
    tag: "cadence",
    summary: "The recurring team meetings the caller attends or may see",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: MeetingSeriesList },
    errors: [],
    emits: [],
  }),
  "meetingSeries.create": defineRoute({
    ...core,
    id: "meetingSeries.create",
    method: "post",
    path: `${base}/meeting-series`,
    tag: "cadence",
    summary: "Add a recurring team meeting and schedule its first meeting",
    access: "viewer",
    params: CompanyParams,
    body: MeetingSeriesCreate,
    idempotency: "optional",
    response: { status: 201, schema: MeetingSeriesView },
    errors: ["validation_failed"],
    emits: ["meeting_series.changed", "meeting.scheduled"],
  }),
  "meetingSeries.update": defineRoute({
    ...core,
    id: "meetingSeries.update",
    method: "patch",
    path: `${base}/meeting-series/{id}`,
    tag: "cadence",
    summary: "Edit or archive a recurring team meeting",
    access: "viewer",
    params: CompanyItemParams,
    body: MeetingSeriesPatch,
    response: { status: 200, schema: MeetingSeriesView },
    errors: ["validation_failed"],
    emits: ["meeting_series.changed"],
  }),
  "meetings.list": defineRoute({
    ...core,
    id: "meetings.list",
    method: "get",
    path: `${base}/meeting-series/{id}/meetings`,
    tag: "cadence",
    summary: "A series' meetings, newest first",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: MeetingList },
    errors: [],
    emits: [],
  }),
  "meetings.create": defineRoute({
    ...core,
    id: "meetings.create",
    method: "post",
    path: `${base}/meeting-series/{id}/meetings`,
    tag: "cadence",
    summary: "Schedule a meeting of a series (default: its next occurrence)",
    access: "viewer",
    params: CompanyItemParams,
    body: MeetingCreate,
    idempotency: "optional",
    response: { status: 201, schema: MeetingDetail },
    errors: ["validation_failed"],
    emits: ["meeting.scheduled"],
  }),
  "meetings.get": defineRoute({
    ...core,
    id: "meetings.get",
    method: "get",
    path: `${base}/meetings/{id}`,
    tag: "cadence",
    summary: "A meeting with its agenda (live source cards), outcomes and pre-read",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: MeetingDetail },
    errors: [],
    emits: [],
  }),
  "meetings.update": defineRoute({
    ...core,
    id: "meetings.update",
    method: "patch",
    path: `${base}/meetings/{id}`,
    tag: "cadence",
    summary: "Write a meeting's notes or pre-read, move its live item, rate it",
    access: "viewer",
    params: CompanyItemParams,
    body: MeetingPatch,
    response: { status: 200, schema: MeetingDetail },
    errors: ["validation_failed"],
    emits: ["meeting.updated", "meeting.rated"],
  }),
  "meetings.buildAgenda": defineRoute({
    ...core,
    id: "meetings.buildAgenda",
    method: "post",
    path: `${base}/meetings/{id}/agenda/build`,
    tag: "cadence",
    summary:
      "Build the agenda from the live data: misses, off-track priorities, issues, due to-dos",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: MeetingDetail },
    errors: ["conflict"],
    emits: ["meeting.agenda_built"],
  }),
  "meetings.addItem": defineRoute({
    ...core,
    id: "meetings.addItem",
    method: "post",
    path: `${base}/meetings/{id}/agenda`,
    tag: "cadence",
    summary: "Add a custom line to a meeting's agenda",
    access: "viewer",
    params: CompanyItemParams,
    body: AgendaItemCreate,
    response: { status: 201, schema: MeetingDetail },
    errors: ["validation_failed", "conflict"],
    emits: ["meeting.agenda_changed"],
  }),
  "meetings.updateItem": defineRoute({
    ...core,
    id: "meetings.updateItem",
    method: "patch",
    path: `${base}/meetings/{id}/agenda/{item_id}`,
    tag: "cadence",
    summary: "Edit an agenda line: title, time box, order, done, elapsed time",
    access: "viewer",
    params: MeetingItemParams,
    body: AgendaItemPatch,
    response: { status: 200, schema: MeetingDetail },
    errors: ["validation_failed"],
    emits: ["meeting.agenda_changed"],
  }),
  "meetings.removeItem": defineRoute({
    ...core,
    id: "meetings.removeItem",
    method: "delete",
    path: `${base}/meetings/{id}/agenda/{item_id}`,
    tag: "cadence",
    summary: "Remove a line from a meeting's agenda",
    access: "viewer",
    params: MeetingItemParams,
    response: { status: 204 },
    errors: ["conflict"],
    emits: ["meeting.agenda_changed"],
  }),
  "meetings.start": defineRoute({
    ...core,
    id: "meetings.start",
    method: "post",
    path: `${base}/meetings/{id}/start`,
    tag: "cadence",
    summary: "Start a meeting (its agenda is built first if it never was)",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: MeetingDetail },
    errors: ["conflict"],
    emits: ["meeting.started", "meeting.agenda_built"],
  }),
  // ACTIONS: a meeting that has not started is cancelled (kept, state `cancelled`); the series
  // goes on: its next occurrence is scheduled when none is after the cancelled one.
  "meetings.cancel": defineRoute({
    ...core,
    id: "meetings.cancel",
    method: "post",
    path: `${base}/meetings/{id}/cancel`,
    tag: "cadence",
    summary: "Cancel a meeting that has not started (the series' next one is scheduled)",
    access: "viewer",
    params: CompanyItemParams,
    idempotency: "optional",
    response: { status: 200, schema: MeetingDetail },
    errors: ["conflict"],
    emits: ["meeting.cancelled", "meeting.scheduled"],
  }),
  "meetings.end": defineRoute({
    ...core,
    id: "meetings.end",
    method: "post",
    path: `${base}/meetings/{id}/end`,
    tag: "cadence",
    summary: "End a meeting: rate it, post its summary, schedule the next one",
    access: "viewer",
    params: CompanyItemParams,
    body: MeetingEnd,
    response: { status: 200, schema: MeetingDetail },
    errors: ["conflict"],
    emits: [
      "meeting.ended",
      "meeting.rated",
      "meeting.updated",
      "message.created",
      "topic.created",
      "meeting.scheduled",
    ],
  }),
  "meetings.recordOutcome": defineRoute({
    ...core,
    id: "meetings.recordOutcome",
    method: "post",
    path: `${base}/meetings/{id}/outcomes`,
    tag: "cadence",
    summary: "Capture an outcome inline: a to-do, a decision, an issue resolved or raised",
    access: "viewer",
    params: CompanyItemParams,
    body: MeetingOutcomeCreate,
    idempotency: "optional",
    response: { status: 201, schema: MeetingDetail },
    errors: ["validation_failed", "conflict"],
    emits: [
      "meeting.outcome_recorded",
      "task.created",
      "decision.recorded",
      "issue.resolved",
      "issue.raised",
    ],
  }),
  "meetings.requestPreread": defineRoute({
    ...core,
    id: "meetings.requestPreread",
    method: "post",
    path: `${base}/meetings/{id}/preread`,
    tag: "cadence",
    summary: "Ask the team's agent for the pre-read now (pending when none is live)",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: MeetingDetail },
    errors: ["conflict"],
    emits: ["meeting.preread_requested", "wakeup.requested"],
  }),
  "vision.get": defineRoute({
    ...core,
    id: "vision.get",
    method: "get",
    path: `${base}/vision`,
    tag: "cadence",
    summary: "The company's vision page, by section, with the quarter's priorities",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: VisionView },
    errors: [],
    emits: [],
  }),
  "vision.update": defineRoute({
    ...core,
    id: "vision.update",
    method: "put",
    path: `${base}/vision`,
    tag: "cadence",
    summary: "Edit the company's vision page (managers)",
    access: "viewer",
    params: CompanyParams,
    body: VisionUpdate,
    response: { status: 200, schema: VisionView },
    errors: ["validation_failed", "conflict"],
    emits: ["vision_page.updated", "page.created", "page.edited"],
  }),
  // RECALL-KPIS-MIRROR (design 2026-10-31 §4.4): agreement or objection on a logged decision.
  "decisionStances.list": defineRoute({
    ...core,
    id: "decisionStances.list",
    method: "get",
    path: `${base}/decisions/{id}/stances`,
    tag: "cadence",
    summary: "Who agreed with or objected to a logged decision",
    description:
      "A decision of the log the caller can see (its meeting's series, its issue's team, or a " +
      "decision Recall reported to a seat they hold; managers see all). Each person's stance, " +
      "the caller's own, and what memory did with it.",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: DecisionStances },
    errors: [],
    emits: [],
  }),
  "decisionStances.set": defineRoute({
    ...core,
    id: "decisionStances.set",
    method: "put",
    path: `${base}/decisions/{id}/stances/me`,
    tag: "cadence",
    summary: "Agree with (ratify) or object to (dissent, with why) a logged decision",
    description:
      "A person's own stance; a change replaces it. The decision binds either way. When the " +
      "decision is in memory, the stance goes to Recall's ratify or dissent door as the person " +
      "(`recall_state`: `sent`, `waiting` when memory does not offer it yet, `refused`, or " +
      "`local` when the decision has no memory record).",
    access: "viewer",
    params: CompanyItemParams,
    body: DecisionStanceBody,
    idempotency: "optional",
    response: { status: 200, schema: DecisionStances },
    errors: ["forbidden", "validation_failed"],
    emits: ["decision.stance_recorded"],
  }),
} as const;

/** Re-exported for the screens: the agenda's sections in the order the builder writes them. */
export const AGENDA_ORDER = [
  "scorecard",
  "priorities",
  "issues",
  "todos",
  "headlines",
  "custom",
] as const satisfies readonly z.infer<typeof AgendaSection>[];

/** Status column order of the issues board. */
export const ISSUE_COLUMNS = ["identify", "discuss", "solve"] as const satisfies readonly z.infer<
  typeof IssueStatus
>[];
